import { useCallback, useEffect, useRef, useState } from "react";
import {
  comboKey,
  layerForCombo,
  layersBindingCode,
  type ModifierState,
  normalizeCombo,
  resolveActiveLayer,
} from "@/domain/profile-resolver/index";
import type { Binding, Layers, RapidFireMarks, RenderKeycap } from "@/domain/types";
import { isZone, ZONE_LEGENDS, type ZoneCode } from "@/domain/zones";
import { legendFor } from "@/hardware/keyboard-layouts/legends";
import { modifierFamily } from "@/hardware/modifier-family";
import { isEditableTarget, isSelectTypeahead } from "@/platform/lib/editable-target";

/** What the readout strip shows for the last pressed or clicked key. */
export interface ReadoutState {
  code: string;
  legend: string;
  binding: Binding | null;
  /** Canonical modifier combo the binding was resolved on; "" means the base layer. */
  combo: string;
  /** The key carries a rapid-fire mark at the exact combo pressed. */
  rapidFire?: boolean;
  /** The OTHER modifier layers that also bind this code: the readout's chips. */
  others: { combo: string; binding: Binding }[];
}

/** Codes whose browser default (scroll, focus move, function row) would fight exploration.
 * A printable key gets the same treatment only over a focused dropdown (isSelectTypeahead). */
const PREVENT = new Set([
  "Tab",
  "Space",
  "Backspace",
  "ArrowUp",
  "ArrowDown",
  "ArrowLeft",
  "ArrowRight",
  "F1",
  "F2",
  "F3",
  "F4",
  "F5",
  "F6",
  "F7",
  "F8",
  "F9",
  "F10",
  "F11",
  "F12",
]);

/**
 * Display legend for a pressed code: a zone's fixed legend (zones have no keycap
 * and no Keyboard Layout entry), else the clicked cap's own legend when
 * given, else the first composed keycap carrying the code (a code may sit on
 * several keys), else the universal fallback, else the raw code.
 */
export function readoutLegend(code: string, keycaps: RenderKeycap[], explicit?: string): string {
  if (isZone(code)) return ZONE_LEGENDS[code as ZoneCode];
  // resolution keys on the cap's active code; a board-layer position finds its cap by the
  // keycode it emits, the same code a physical keypress reports
  const fromCap = explicit || keycaps.find((k) => k.code === code)?.legend;
  return fromCap || legendFor(code) || code;
}

export interface ReadoutOptions {
  /** The Profile's layers; every press resolves exact-combo through them. */
  layers: Layers;
  /** Active modifier families (held or latched). */
  mods: ModifierState;
  keycaps: RenderKeycap[];
  /** Rapid-fire marks when the overlay is on, else null; tags a press. */
  marks?: RapidFireMarks | null;
  /** Lens identity (Keyboard + Keyboard Layout ids); a new lens clears the readout. */
  lensKey: string;
  /** Pass false while a dialog owns the keyboard, so Tab/Space keep working there. */
  enabled?: boolean;
}

/**
 * Readout state for the visualizer: physical keydown plus a press() hook for keycap
 * clicks. Resolution is exact-combo through the active modifier state. A modifier
 * key resolves with its own family stripped, so pressing Shift reads out what Shift
 * does (base layer) rather than the shift layer's missing ShiftLeft entry.
 */
export function useReadout({
  layers,
  mods,
  keycaps,
  marks = null,
  lensKey,
  enabled = true,
}: ReadoutOptions) {
  const [readout, setReadout] = useState<ReadoutState | null>(null);

  // a new lens (board switch, import) invalidates the shown legend; reset during
  // render, the React pattern for adjusting state when props change. Keyed on the
  // lens identity, not the keycaps array: modifier swaps recompose the keycaps
  // every press and must not wipe the readout the same keystroke just set
  const [prevLens, setPrevLens] = useState(lensKey);
  if (prevLens !== lensKey) {
    setPrevLens(lensKey);
    setReadout(null);
  }

  const press = useCallback(
    (code: string, legend?: string) => {
      // strip the family the press itself activates, so pressing Shift reads out what Shift does
      // (base layer) rather than the shift layer's missing ShiftLeft entry. `code` is the key's
      // active code, which equals the raw firmware code a physical keydown reports.
      const family = modifierFamily(code);
      const target = family ? { ...mods, [family]: false } : mods;
      const held = comboKey(target);
      const resolved = resolveActiveLayer(layers, target)[code];
      const combo = resolved?.combo ?? held;
      const next: ReadoutState = {
        code,
        legend: readoutLegend(code, keycaps, legend),
        binding: resolved?.binding ?? null,
        // a bound key names its source combo (a fallback reads as base,
        // so "Y - Patrol" not "Shift+Y"); an unbound key names the combo held
        combo,
        // every other layer that binds this code; the chips that latch + read out
        others: layersBindingCode(layers, code).filter((o) => o.combo !== combo),
      };
      // marks are exact-combo only: a base mark must not tag while a modifier is held
      if (marks?.[held]?.has(code)) next.rapidFire = true;
      setReadout(next);
    },
    [layers, mods, keycaps, marks],
  );

  // the bindings-list path: a row names its own combo, so resolution ignores the live
  // modifier state; the readout's combo badge tells the player which modifier layer it is
  const pressCombo = useCallback(
    (code: string, combo: string) => {
      const norm = normalizeCombo(combo);
      const next: ReadoutState = {
        code,
        legend: readoutLegend(code, keycaps),
        binding: layerForCombo(layers, combo)[code] ?? null,
        combo: norm,
        others: layersBindingCode(layers, code).filter((o) => o.combo !== norm),
      };
      if (marks?.[norm]?.has(code)) next.rapidFire = true;
      setReadout(next);
    },
    [layers, keycaps, marks],
  );

  // Subscribe the physical-keydown listener ONCE and read the live handlers from refs, rather than
  // re-subscribing whenever press/keycaps/mods change. Re-subscribing on every modifier press raced
  // the event: a key pressed during the re-subscribe could miss the listener entirely (a held Ctrl
  // read as "Unbound"). The refs always hold the latest closures. (The held-press depress lives in
  // useModifiers' single `held` set; here we only drive the readout.)
  const pressRef = useRef(press);
  pressRef.current = press;
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (!enabledRef.current || isEditableTarget(e.target)) return;
      // a physical keypress reports the key's ACTIVE code - exactly the code the resolved layers are
      // keyed by, so it resolves directly with no translation. A printable key over a focused
      // dropdown is prevented for the same reason the scroll keys are: its default (the select's
      // type-ahead) would change what is shown instead of letting the key reach the board.
      if (PREVENT.has(e.code) || isSelectTypeahead(e)) e.preventDefault();
      // auto-repeat would re-set identical state; preventDefault above still runs so a held arrow or
      // Space does not start scrolling mid-hold
      if (!e.repeat) pressRef.current(e.code);
    };
    window.addEventListener("keydown", down);
    return () => window.removeEventListener("keydown", down);
  }, []);

  return { readout, press, pressCombo };
}
